import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Clock, Mail, Send, XCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact email-history panel rendered inside any WO/Proposal/CO editor.
 *
 * Reads /api/email-log/{ref_type}/{ref_id} (newest first) and renders a
 * vertical list of every automated email sent for that doc — `sent`,
 * `delivered`, `bounced`, `delayed`, `complained`, plus the local-only
 * statuses `skipped_feature_disabled` / `skipped_no_api_key` / `error`.
 *
 * The endpoint includes rows even when the email never went out (skip
 * paths) so the user can see "we tried but customer had no email on
 * file" — useful audit signal that doesn't require digging into Mongo.
 *
 * Empty list → render nothing (don't clutter the editor with "no
 * emails yet"). The Phase 1 / Phase 2 plan keeps this component self-
 * contained so it can drop into any of the 3 editor surfaces without
 * touching their existing code.
 */
const STATUS_BADGES = {
  sent:           { label: "Sent",         cls: "bg-blue-50 text-blue-700 border-blue-200",   icon: Send },
  delivered:      { label: "Delivered",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  delayed:        { label: "Delayed",      cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  bounced:        { label: "Bounced",      cls: "bg-rose-50 text-rose-700 border-rose-200",   icon: XCircle },
  complained:     { label: "Complained",   cls: "bg-rose-50 text-rose-700 border-rose-200",   icon: AlertTriangle },
  opened:         { label: "Opened",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  clicked:        { label: "Clicked",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  error:          { label: "Send Error",   cls: "bg-rose-50 text-rose-700 border-rose-200",   icon: XCircle },
  skipped_feature_disabled: { label: "Skipped (off)", cls: "bg-slate-50 text-slate-500 border-slate-200", icon: Clock },
  skipped_no_api_key:       { label: "Skipped (no key)", cls: "bg-slate-50 text-slate-500 border-slate-200", icon: Clock },
};

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function EmailHistoryPanel({ refType, refId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!refType || !refId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/email-log/${refType}/${refId}`)
      .then(res => { if (!cancelled) setItems(res.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refType, refId]);

  if (loading) return null;
  if (!items.length) return null;

  return (
    <div className="border border-slate-200 rounded-lg bg-slate-50/60 p-3 space-y-2" data-testid="email-history-panel">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold tracking-wider text-slate-500 uppercase flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" /> Automated Emails
        </span>
        <span className="text-[10px] text-slate-400">{items.length} {items.length === 1 ? "send" : "sends"}</span>
      </div>
      <div className="space-y-1.5">
        {items.map((row) => {
          const badge = STATUS_BADGES[row.status] || { label: row.status, cls: "bg-slate-50 text-slate-600 border-slate-200", icon: Mail };
          const Icon = badge.icon;
          const deliveredAt = row.events?.delivered_at;
          const bouncedAt = row.events?.bounced_at;
          return (
            <div key={row.id} className="flex items-start gap-2 bg-white border border-slate-200 rounded p-2 text-xs" data-testid={`email-log-${row.id}`}>
              <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-medium ${badge.cls}`}>
                <Icon className="w-3 h-3" />
                {badge.label}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-slate-700 truncate">
                  <span className="font-medium">{row.subject || "(no subject)"}</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>To: {row.to}</span>
                  {row.reply_to && <span>Reply: {row.reply_to}</span>}
                  <span>{fmtDateTime(row.created_at)}</span>
                  {deliveredAt && <span className="text-emerald-600">Delivered {fmtDateTime(deliveredAt)}</span>}
                  {bouncedAt && <span className="text-rose-600">Bounced {fmtDateTime(bouncedAt)}{row.bounce_message ? ` — ${row.bounce_message}` : ""}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
